import styled, { css } from 'styled-components';
import { getTextAlign } from '@applyft-web/ui-components';

interface CommonProps {
  readonly $inverted?: boolean;
}

export const Stepper = styled.ol<CommonProps>(
  ({ theme, $inverted }) => css`
    --counter-size: 48px;
    --side-padding: ${$inverted ? '0px' : '16px'};

    list-style: none;
    margin: 16px auto 0;
    padding: 0 var(--side-padding);
    width: 100%;
    position: relative;
    isolation: isolate;
    counter-reset: my-sec-counter;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: calc((var(--counter-size) / 2));
      ${getTextAlign(theme.isRtl)}: calc((var(--counter-size) / 2) + var(--side-padding));

      width: 2px;
      height: calc(100% - var(--counter-size));
      background: repeating-linear-gradient(
        to bottom,
        ${theme?.colors?.primary},
        ${theme?.colors?.primary} 4px,
        ${$inverted ? 'black' : theme?.custom?.background || theme?.colors?.bodyBackground} 4px,
        ${$inverted ? 'black' : theme?.custom?.background || theme?.colors?.bodyBackground} 8px
      );
      z-index: -1;
    }

    ${
      !$inverted &&
      css`
        @media screen and (min-width: ${theme?.desktopMinWidth}px) {
          --side-padding: 0px;

          display: flex;
          padding-top: 24px;

          &::before {
            display: none;
          }
        }
      `
    };
  `,
);

export const StepperItem = styled.li<CommonProps>(
  ({ theme, $inverted }) => css`
    position: relative;
    display: flex;
    align-items: center;

    &::before {
      counter-increment: my-sec-counter;
      content: counter(my-sec-counter);
      font-weight: 600;
      font-size: 24px;
      line-height: 32px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #f2994a;
      flex: 0 0 var(--counter-size);
      height: var(--counter-size);
      border: 1px solid ${theme?.custom?.stepBorder || '#FCB475'};
      border-radius: 8px;
      background-color: ${({ theme }) => theme?.custom?.trustLabelsBg || '#FEF3EB'};
      ${({ theme }) => css`
        margin-${theme.isRtl ? 'left' : 'right'}: 16px;
      `};
    }

    ${
      !$inverted &&
      css`
        @media screen and (max-width: ${theme?.desktopMinWidth}px) {
          &:first-child {
            align-items: flex-start;
          }

          &:last-child {
            align-items: flex-end;
          }
        }

        @media screen and (min-width: ${theme?.desktopMinWidth}px) {
          align-items: flex-start;

          &:not(:last-child) {
            padding-bottom: 0;
            ${({ theme }) => css`
            margin-${theme.isRtl ? 'left' : 'right'}: 32px;
          `};
          }
        }
      `
    };

    &:not(:last-child) {
      padding-bottom: 24px;
    }
  `,
);

export const StepperContent = styled.div<CommonProps>`
  text-align: ${({ theme }) => getTextAlign(theme.isRtl)};
`;

export const StepperTitle = styled.div<CommonProps>`
  color: ${({ $inverted, theme }) => theme.custom?.bodyColor || theme?.colors?.[$inverted ? 'textSecondary' : 'text']};
  font-weight: 600;
  font-size: 18px;
  line-height: 23px;
  margin: 0;
  overflow-wrap: break-word;
`;

export const StepperDesc = styled.div`
  color: ${({ theme }) => theme.custom?.secondary || '#5B5B5B'};
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
`;
